import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertTriangle, Truck, Package, ShoppingCart, User, Calendar, FileText, Mail,
  Database, Hash, Receipt, CreditCard, RefreshCw,
} from 'lucide-react';
import api from '../../services/api';
import AggiornaDalGestionale from '../../components/admin/AggiornaDalGestionale';
import type { MyOrdine } from '../../hooks/useMyOrdini';
import { statoOrdine, statoRiga } from '../../utils/statoOrdine';

/**
 * Dettaglio di un Ordine web per l'admin. Righe, totali, stato, consegna e
 * pagamento sono **quelli del gestionale**, gli stessi che vede il Cliente
 * (`GET /api/orders/admin/{id}/dettaglio`). Dal portale arriva solo ciò che
 * NTS non sa: numero portale, utente, esito dell'invio, annullo, anomalie.
 * Lo stato non si cambia da qui: lo decide il gestionale.
 */

function fmt(n: number | undefined | null) {
  if (n == null) return '—';
  return `€ ${Number(n).toFixed(2).replace('.', ',')}`;
}
function fmtDateTime(iso: string | undefined | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso?.slice(0, 16) ?? '—'; }
}
function fmtDate(iso: string | undefined | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT'); } catch { return iso; }
}

const ERP_DOT: Record<string, { label: string; dot: string; bg: string }> = {
  ok:        { label: 'Registrato in ERP', dot: 'bg-green-500', bg: 'bg-green-50 text-green-700 border-green-200' },
  error:     { label: 'Errore ERP',        dot: 'bg-red-500',   bg: 'bg-red-50 text-red-700 border-red-200' },
  skipped:   { label: 'Saltato',           dot: 'bg-gray-400',  bg: 'bg-gray-50 text-gray-700 border-gray-200' },
  cancelled: { label: 'Annullato in ERP',  dot: 'bg-gray-400',  bg: 'bg-gray-50 text-gray-700 border-gray-200' },
};

/** Stati del portale per un ordine che il gestionale non ha (mai arrivato o rifiutato). */
const STATI_PORTALE: Record<number, string> = { 5: 'Annullato', 7: 'Errore: non registrato sul gestionale' };

interface OrdinePortale {
  idOrdine: number;
  guid: string;
  numeroOrdine: string;
  /** Il numero del gestionale (`OW/2026/00048`), null se non è su NTS (INT-134). */
  numeroGestionale?: string | null;
  idUser: string;
  idCliente?: number;
  idStatoOrdine: number;
  totaleOrdine: number;
  imponibile: number;
  speseSpedizione?: number;
  dataOrdine: string;
  attesaPagamento?: boolean;
  nexiCodTrans?: string | null;
  items: { sku: string; title: string; qty: number; totaleLinea: number }[];
  erp?: { status: string; key?: any; errors?: string[]; at?: string; rollback?: string; pagatoSuGestionale?: boolean } | null;
  annullamento?: { motivo: string; codice?: string; descrizione: string; da: string; codTrans?: string | null; at: string } | null;
  anomaliaGestionale?: { imponibilePortale: number; merceGestionale: number; differenza: number;
    righe: { codart: string; portale: number | null; gestionale: number | null }[] } | null;
  _userInfo?: { ragioneSociale: string; email: string; idCliente?: number };
}

interface Dettaglio {
  ordine: OrdinePortale;
  gestionale: MyOrdine | null;
  gestionaleMotivo: string | null;
}

function Field({ label, value, icon: Icon, mono }: {
  label: string; value: React.ReactNode; icon?: React.ElementType; mono?: boolean;
}) {
  const isEmpty = value === undefined || value === null || value === '';
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-secondary/60 last:border-b-0">
      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${isEmpty ? 'text-muted-foreground italic' : 'text-foreground'}`}>
        {isEmpty ? '—' : value}
      </span>
    </div>
  );
}

function Card({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
        <Icon className="w-4 h-4 text-[hsl(214,58%,41%)]" />
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function AdminOrderDetailPage() {
  const { idOrdine } = useParams<{ idOrdine: string }>();
  const [data, setData] = useState<Dettaglio | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!idOrdine) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res = await api.get<Dettaglio>(`/api/orders/admin/${idOrdine}/dettaglio`);
      setData(res.data);
    } catch (err: any) {
      if (err?.response?.status === 404) setNotFound(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [idOrdine]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-order-detail-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[hsl(214,58%,41%)]" />
          <p className="text-sm text-muted-foreground">Caricamento ordine…</p>
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-order-detail-not-found">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h1 className="text-xl font-semibold mb-2">Ordine non trovato</h1>
          <p className="text-sm text-muted-foreground mb-5">
            L'ordine <span className="font-mono">{idOrdine}</span> non esiste o non è accessibile.
          </p>
          <Link
            to="/admin/ordini"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(206,54%,28%)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna agli ordini
          </Link>
        </div>
      </div>
    );
  }

  const { ordine, gestionale: g, gestionaleMotivo } = data;
  const items = g?.items ?? [];
  const totalQty = items.reduce((s, i) => s + i.quantita, 0);
  const totalEvaso = items.reduce((s, i) => s + (i.quantitaEvasa || 0), 0);
  const stato = g ? statoOrdine(g.status) : null;
  const erpInfo = ordine.erp ? (ERP_DOT[ordine.erp.status] ?? { label: ordine.erp.status, dot: 'bg-gray-400', bg: 'bg-gray-50 text-gray-700 border-gray-200' }) : null;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-order-detail-page">
      <Link
        to="/admin/ordini"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        data-testid="admin-order-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Torna agli ordini
      </Link>

      {/* Header */}
      <div className="rounded-xl border bg-white shadow-sm p-4 sm:p-5 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[11px] font-medium text-muted-foreground tracking-wide font-mono" data-testid="admin-order-detail-numero-web">web {ordine.numeroOrdine}</span>
              {stato ? (
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${stato.badge}`} data-testid="admin-order-detail-stato">
                  <span className={`w-1.5 h-1.5 rounded-full ${stato.dot}`} />{stato.label}
                </span>
              ) : (
                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border bg-gray-100 text-gray-700 border-gray-200" data-testid="admin-order-detail-stato">
                  {STATI_PORTALE[ordine.idStatoOrdine] ?? 'Non sul gestionale'}
                </span>
              )}
              {ordine.attesaPagamento && (
                <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">In attesa di pagamento con carta</span>
              )}
              {erpInfo && (
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full border ${erpInfo.bg}`} data-testid="admin-order-detail-erp-badge">
                  <span className={`w-1.5 h-1.5 rounded-full ${erpInfo.dot}`} />{erpInfo.label}
                </span>
              )}
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight" data-testid="admin-order-detail-title">
              <ShoppingCart className="w-5 h-5 inline-block mr-2 -mt-0.5 text-[hsl(214,58%,41%)]" />
              Ordine {g?.id ?? ordine.numeroGestionale ?? `web ${ordine.numeroOrdine}`}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span><Calendar className="w-3 h-3 inline-block mr-1" />{g ? fmtDate(g.date) : fmtDateTime(ordine.dataOrdine)}</span>
              {g && (
                <>
                  <span>•</span>
                  <span><strong className="text-foreground">{items.length}</strong> righe ({totalQty} pz)</span>
                  <span>•</span>
                  <span><strong className="text-foreground">{fmt(g.totaleIvato)}</strong> IVA inclusa</span>
                </>
              )}
            </div>
            {!g && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="admin-order-detail-no-gestionale">
                {gestionaleMotivo ?? 'Dati del gestionale non disponibili.'} Sotto, solo quanto chiesto dal cliente sul portale.
              </div>
            )}
            {ordine.annullamento && (
              <div className="mt-3 rounded-lg border bg-gray-50 px-3 py-2 text-xs text-gray-700" data-testid="admin-order-detail-annullamento">
                <strong>Annullato {ordine.annullamento.da === 'gestionale' ? 'sul gestionale' : 'dal portale'}</strong>
                {ordine.annullamento.codice && <> (<span className="font-mono">{ordine.annullamento.codice}</span>)</>}
                {' '}il {fmtDateTime(ordine.annullamento.at)}: {ordine.annullamento.descrizione}
                {ordine.annullamento.codTrans && <> · pagamento <span className="font-mono">{ordine.annullamento.codTrans}</span></>}
                {ordine.erp?.rollback === 'failed' && <span className="block text-red-700 font-medium mt-1">L&apos;annullo su NTS non è riuscito: l&apos;ordine va annullato a mano sul gestionale.</span>}
              </div>
            )}
            {ordine.anomaliaGestionale && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="admin-order-detail-anomalia">
                <strong>Prezzo sul gestionale diverso da quello confermato dal cliente.</strong> Merce NTS {fmt(ordine.anomaliaGestionale.merceGestionale)},
                {' '}confermata {fmt(ordine.anomaliaGestionale.imponibilePortale)} (differenza {fmt(ordine.anomaliaGestionale.differenza)}).
                {' '}Da verificare e sistemare dal backoffice.
                {ordine.anomaliaGestionale.righe.length > 0 && (
                  <ul className="mt-1 list-disc pl-4">
                    {ordine.anomaliaGestionale.righe.map(r => (
                      <li key={r.codart}><span className="font-mono">{r.codart}</span>: portale {r.portale != null ? fmt(r.portale) : '—'}, gestionale {r.gestionale != null ? fmt(r.gestionale) : '—'}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          {ordine.numeroGestionale ? (
            <div className="self-start">
              <AggiornaDalGestionale numeroOrdine={ordine.numeroGestionale} onFatto={load} testId="admin-order-detail-reload" />
            </div>
          ) : (
            <button
              onClick={load}
              className="h-9 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 hover:bg-secondary/60 self-start"
              data-testid="admin-order-detail-reload"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Ricarica
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <Card title="Cliente" icon={User}>
          <Field label="Ragione sociale" value={ordine._userInfo?.ragioneSociale || ordine._userInfo?.email} />
          <Field label="Email" value={ordine._userInfo?.email} icon={Mail} />
          <Field label="Conto NTS" value={g?.anConto ?? ordine.idCliente ?? ordine._userInfo?.idCliente} mono />
          <Field label="ID Utente" value={ordine.idUser} mono />
        </Card>

        <Card title="Totali" icon={Receipt}>
          {g ? (
            <div className="space-y-2 text-sm" data-testid="admin-order-detail-totali">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotale</span><span className="tabular-nums font-medium">{fmt(g.totalNet)}</span></div>
              {g.speseSpedizione > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Spese d&apos;incasso</span><span className="tabular-nums font-medium">{fmt(g.speseSpedizione)}</span></div>
              )}
              <div className="border-t pt-2 mt-1 flex justify-between"><span className="text-muted-foreground">Totale IVA esclusa</span><span className="tabular-nums font-medium">{g.totale != null ? fmt(g.totale) : '—'}</span></div>
              <div className="flex justify-between text-base">
                <span className="font-semibold">Totale <span className="text-xs font-normal text-muted-foreground">IVA inclusa</span></span>
                <span className="tabular-nums font-bold text-[hsl(214,58%,41%)]">{fmt(g.totaleIvato)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t">
                <span>Evaso</span>
                <span className={totalEvaso >= totalQty ? 'text-green-600 font-semibold' : 'text-amber-600 font-semibold'}>
                  {totalEvaso}/{totalQty} pz
                </span>
              </div>
            </div>
          ) : (
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Totale chiesto sul portale (IVA esclusa)</span><span className="tabular-nums font-medium">{fmt(ordine.imponibile + (ordine.speseSpedizione || 0))}</span></div>
            </div>
          )}
        </Card>

        <Card title="ERP NTS" icon={Database}>
          {!ordine.erp ? (
            <p className="text-xs text-muted-foreground italic">Nessun invio al gestionale.</p>
          ) : (
            <>
              <Field label="Numero NTS" value={g?.id ?? (ordine.erp.key ? `${ordine.erp.key.et_serie}/${ordine.erp.key.et_anno}/${ordine.erp.key.et_numdoc}` : null)} mono />
              <Field label="Inviato il" value={fmtDateTime(ordine.erp.at)} />
              {ordine.erp.errors && ordine.erp.errors.length > 0 && (
                <div className="mt-3 p-2 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700">
                  <strong className="block mb-1">Errori ERP:</strong>
                  <ul className="list-disc list-inside space-y-0.5">
                    {ordine.erp.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {g && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <Card title="Consegna" icon={Truck}>
            <Field label="Destinazione" value={g.destination} />
            <Field label="Vettore" value={g.carrier} />
          </Card>
          <Card title="Pagamento" icon={CreditCard}>
            <Field label="Condizione di pagamento" value={g.paymentMethod} />
            {ordine.nexiCodTrans && <Field label="Pagamento con carta" value={ordine.nexiCodTrans} mono />}
          </Card>
        </div>
      )}

      {/* Righe: quelle del gestionale, come le vede il Cliente */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden mb-5">
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
          <Package className="w-4 h-4 text-[hsl(214,58%,41%)]" />
          <h2 className="text-sm font-semibold tracking-tight">
            {g ? `Righe sul gestionale (${items.length})` : `Articoli chiesti sul portale (${ordine.items.length})`}
          </h2>
        </div>
        <div className="overflow-x-auto">
          {g ? (
            <table className="w-full text-sm" data-testid="admin-order-detail-items-table">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Codice</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Prodotto</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Qtà</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Prezzo</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Sconto</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Totale</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Evaso</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.riga} className="border-b last:border-b-0 hover:bg-secondary/20 transition-colors" data-testid="admin-order-detail-item-row">
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-[hsl(214,58%,41%)] whitespace-nowrap">
                      <Link to={`/admin/prodotti/${encodeURIComponent(it.sku)}`} className="hover:underline">{it.sku}</Link>
                    </td>
                    <td className="px-3 py-2.5 font-medium">{it.title}</td>
                    <td className="px-3 py-2.5 text-center font-semibold tabular-nums">{it.quantita}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(it.prezzoUnitario)}</td>
                    <td className="px-3 py-2.5 text-center">
                      {it.scont1 > 0 || it.scont2 > 0 ? (
                        <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                          -{[it.scont1, it.scont2].filter(x => x > 0).map(x => `${x.toLocaleString('it-IT')}%`).join(' + ')}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{fmt(it.totaleRiga)}</td>
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <span className="text-xs font-semibold tabular-nums mr-1.5">{it.quantitaEvasa || 0}/{it.quantita}</span>
                      {(() => {
                        // Stato della riga (INT-131), non solo le quantità.
                        const s = statoRiga(it, g?.status);
                        return <span className={`text-[10px] font-semibold px-1.5 py-px rounded border ${s.badge}`}>{s.label}</span>;
                      })()}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-4 text-center text-xs text-muted-foreground italic">Il gestionale non ha reso le righe: riprova con «Ricarica dal gestionale».</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="admin-order-detail-items-portale">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Codice</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Prodotto</th>
                  <th className="text-center px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Qtà</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Totale</th>
                </tr>
              </thead>
              <tbody>
                {ordine.items.map((it, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="px-3 py-2.5 font-mono text-xs">{it.sku}</td>
                    <td className="px-3 py-2.5">{it.title}</td>
                    <td className="px-3 py-2.5 text-center tabular-nums">{it.qty}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(it.totaleLinea)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {g?.note && (
        <Card title="Note sul gestionale" icon={FileText}>
          <p className="text-sm whitespace-pre-wrap">{g.note}</p>
        </Card>
      )}

      <div className="mt-5 text-[10px] text-muted-foreground flex flex-wrap gap-3">
        <span><Hash className="w-3 h-3 inline-block mr-0.5" />ID Ordine portale: <span className="font-mono">{ordine.idOrdine}</span></span>
        <span>GUID: <span className="font-mono">{ordine.guid}</span></span>
      </div>
    </div>
  );
}
